import { acceptHMRUpdate, defineStore } from 'pinia';
import type { CurrentUser } from '@/types/One';
import { oneApi } from '@/utils/api';
import tokenStorage from '@/utils/tokenStorage';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as CurrentUser | null,
    // The stored session has been checked against /me
    ready: false,
  }),
  getters: {
    isLogged: (state) => !!state.user,
    isAdmin: (state) => !!state.user?.is_admin,
    initials: (state) => {
      const user = state.user;
      if (!user) {
        return '';
      }
      const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
      return (initials || user.user_name.slice(0, 2)).toUpperCase();
    },
  },
  actions: {
    async init() {
      if (tokenStorage.getAccess()) {
        try {
          this.user = await oneApi.me();
        } catch {
          // Expired session: the http client already cleared the tokens it could not refresh
          this.user = null;
        }
      }
      this.ready = true;
    },
    async login(credentials: { user_name: string; password: string }) {
      const { access, refresh, user } = await oneApi.login(credentials);
      tokenStorage.setTokens({ access, refresh });
      this.user = user;
    },
    async logout() {
      try {
        if (tokenStorage.getAccess()) {
          await oneApi.logout(tokenStorage.getRefresh());
        }
      } catch {
        // The tokens are dropped locally anyway
      }
      this.clear();
    },
    clear() {
      tokenStorage.clear();
      this.user = null;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
